import { ApiModel } from '@composable-api/api.model'
import { ComplaintStatus, type ComplaintType } from '@sim-api-enums/complaints'
import type { ApiDateTime, FormattedCurrency, TaxPerRate } from '@sim-api-types/general-data'
import type { GetPriceOptions } from '@simploshop-models/product.model'
import { getFormattedPrice } from '@sim-api-utils/localization'

interface Attributes {
    [ComplaintModel.ATTR_ID]: number
    [ComplaintModel.ATTR_CUSTOMER_ID]: number | null
    [ComplaintModel.ATTR_FIRST_NAME]: string
    [ComplaintModel.ATTR_LAST_NAME]: string
    [ComplaintModel.ATTR_EMAIL]: string
    [ComplaintModel.ATTR_PHONE]: string
    [ComplaintModel.ATTR_BANK_ACCOUNT_NUMBER]: string | null
    [ComplaintModel.ATTR_BANK_CODE]: string | null
    [ComplaintModel.ATTR_BANK_ACCOUNT_IBAN]: string | null
    [ComplaintModel.ATTR_BANK_SWIFT_CODE]: string | null
    [ComplaintModel.ATTR_DOCUMENT_NUMBER]: string
    [ComplaintModel.ATTR_STATE]: ComplaintStatus
    [ComplaintModel.ATTR_TYPE]: ComplaintType
    [ComplaintModel.ATTR_PRICE]: FormattedCurrency
    [ComplaintModel.ATTR_TAXED_PRICE]: FormattedCurrency
    [ComplaintModel.ATTR_CREATED_AT]: ApiDateTime
}

interface Embeds {
    [ComplaintModel.EMBED_TAX_PER_RATE]: TaxPerRate
}

export class ComplaintModel extends ApiModel<Attributes, Embeds> {
    static key = 'ComplaintModel'

    static readonly ATTR_ID = 'id'
    static readonly ATTR_CUSTOMER_ID = 'customer_id'
    static readonly ATTR_FIRST_NAME = 'first_name'
    static readonly ATTR_LAST_NAME = 'last_name'
    static readonly ATTR_EMAIL = 'email'
    static readonly ATTR_PHONE = 'phone'
    static readonly ATTR_BANK_ACCOUNT_NUMBER = 'bank_account_number'
    static readonly ATTR_BANK_CODE = 'bank_code'
    static readonly ATTR_BANK_ACCOUNT_IBAN = 'bank_account_iban'
    static readonly ATTR_BANK_SWIFT_CODE = 'bank_swift_code'
    static readonly ATTR_DOCUMENT_NUMBER = 'document_number'
    static readonly ATTR_STATE = 'state'
    static readonly ATTR_TYPE = 'type'
    static readonly ATTR_PRICE = 'price'
    static readonly ATTR_TAXED_PRICE = 'taxed_price'
    static readonly ATTR_CREATED_AT = 'created_at'

    static readonly EMBED_TAX_PER_RATE = 'tax_per_rate'

    get id() {
        return this._getAttribute(ComplaintModel.ATTR_ID)
    }

    get customerId() {
        return this._getAttribute(ComplaintModel.ATTR_CUSTOMER_ID)
    }

    get firstName() {
        return this._getAttribute(ComplaintModel.ATTR_FIRST_NAME)
    }

    get lastName() {
        return this._getAttribute(ComplaintModel.ATTR_LAST_NAME)
    }

    get email() {
        return this._getAttribute(ComplaintModel.ATTR_EMAIL)
    }

    get phone() {
        return this._getAttribute(ComplaintModel.ATTR_PHONE)
    }

    get bankAccountNumber() {
        return this._getAttribute(ComplaintModel.ATTR_BANK_ACCOUNT_NUMBER)
    }

    get bankCode() {
        return this._getAttribute(ComplaintModel.ATTR_BANK_CODE)
    }

    get bankAccountIban() {
        return this._getAttribute(ComplaintModel.ATTR_BANK_ACCOUNT_IBAN)
    }

    get bankSwiftCode() {
        return this._getAttribute(ComplaintModel.ATTR_BANK_SWIFT_CODE)
    }

    get documentNumber() {
        return this._getAttribute(ComplaintModel.ATTR_DOCUMENT_NUMBER)
    }

    get state() {
        return this._getAttribute(ComplaintModel.ATTR_STATE)
    }

    get type() {
        return this._getAttribute(ComplaintModel.ATTR_TYPE)
    }

    get price() {
        return this._getAttribute(ComplaintModel.ATTR_PRICE)
    }

    get taxedPrice() {
        return this._getAttribute(ComplaintModel.ATTR_TAXED_PRICE)
    }

    get createdAt() {
        return this._getAttribute(ComplaintModel.ATTR_CREATED_AT)
    }

    private get taxPerRate() {
        return this._getEmbed(ComplaintModel.EMBED_TAX_PER_RATE)
    }

    getPrice(options?: Partial<GetPriceOptions>) {
        return (options?.tax !== false ? this.taxedPrice : this.price) ?? null
    }

    getFormattedPrice(options?: Partial<GetPriceOptions>) {
        return getFormattedPrice(this.getPrice(options))
    }

    getTaxPerRatePairs() {
        return Object.values(this.taxPerRate ?? {}).map(entry => ({
            rateFormatted: entry.tax_rate.formatted,
            rate: entry.tax_rate.value,
            amountFormatted: entry.tax_amount.formatted,
            amount: entry.tax_amount.value,
        }))
    }
}
